import { STATIC_EXPERIMENTS } from './';
import { ExperimentVariantCount } from './variantConfig';
import type { ExperimentConfig } from './types';

function getExperiment(pathname: string) {
  return STATIC_EXPERIMENTS.find((config: ExperimentConfig) => {
    return config.paths.includes(pathname);
  });
}

/**
 * Normalizes an experiment config to its total variant count (control included).
 * `variantCount` is the source of truth; the deprecated `isMultiVariate: true` is treated
 * as an A/B/C test. A config with neither is a binary A/B. Returns 0 when the config is
 * absent (no experiment for the path).
 */
export function getVariantCount(experiment: ExperimentConfig | undefined): number {
  if (!experiment) {
    return 0;
  }
  if (experiment.variantCount) {
    return experiment.variantCount;
  }
  if (experiment.isMultiVariate === true) {
    return ExperimentVariantCount.Three;
  }
  return ExperimentVariantCount.Two;
}

/**
 * Total variant count (control included) for the experiment running on the given pathname
 * (0 when no experiment is configured for that path).
 */
export function getVariantCountForPath(pathname: string): number {
  return getVariantCount(getExperiment(pathname));
}

/**
 * Number of treatment variants (excluding control) for the experiment on the given
 * pathname (0 when no experiment is configured). Used to slice the ordered treatment list.
 */
export function getTreatmentCountForPath(pathname: string): number {
  const variantCount = getVariantCountForPath(pathname);
  return variantCount > 0 ? variantCount - 1 : 0;
}

/**
 * The function determines if the test for the pathname is a multivariate test
 * (A/B/C, A/B/C/D, or A/B/C/D/E — three or more variants).
 */
export function isMultiVariateExperiment(pathname: string) {
  return getVariantCountForPath(pathname) >= ExperimentVariantCount.Three;
}

/**
 * The function determines if the test for the pathname is an A/B test
 * (two variants: `control` and `treatment`).
 */
export function isBinaryExperiment(pathname: string) {
  return getVariantCountForPath(pathname) === ExperimentVariantCount.Two;
}
